import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { UserPlusIcon, ArrowDownIcon } from "@heroicons/react/24/solid";
import {
  Card,
  CardHeader,
  Input,
  Typography,
  Button,
  CardBody,
  Chip,
  CardFooter,
  IconButton,
  Select,
  Option,
  Dialog,
  Tooltip,
} from "@material-tailwind/react";
import { useEffect, useState } from "react";
import {
  createTrainingData,
  getTrainingData,
} from "../../helperFunctions/apiFunction";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";
import TableSkeleton from "../../components/Loaders/TableSkeleton";
import toast from "react-hot-toast";
import LoadingButton from "../../components/Loaders/LoadingButton";
import ExcelJS from "exceljs";
import TableRowWithShowMore from "../../components/TableRowWithShowMore";
import TrainingPdfView from "../../components/views/TrainingPdfView";
import moment from "moment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const TABLE_HEAD = [
  "Title",
  "Description",
  "Effective From",
  "Completion Date",
  "Status",
  // "Scope",
  "Mandatory",
];

const getStatusColor = (status) => {
  switch (status) {
    case "schedule":
      return "blue";
    case "inProgress":
      return "orange";
    case "completed":
      return "green";
    case "onHold":
      return "yellow";
    case "cancelled":
      return "red";
    default:
      return "blue-gray";
  }
};

export default function TrainingManagement() {
  const { refreshToken, rbac } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const [pdfView, setPdfView] = useState(false);

  const [isLoading, setIsLoading] = useState(null);
  const [searchBy, setSearchBy] = useState("");
  const [sortBy, setSortBy] = useState("asc");
  const [status, setStatus] = useState("");
  const [mandatory, setMandatory] = useState("");
  const [pdfData, setPdfData] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [errors, setErrors] = useState({ name: "" });
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog((cur) => !cur);
  };

  const handleSelect = (item) => {
    setPdfView(true);
    setPdfData(item);
  };

  useEffect(() => {
    getTrainingData(
      refreshToken,
      searchBy,
      sortBy,
      limit,
      page,
      status,
      mandatory
    ).then((data) => {
      if (data?.code === 440)
        toast.error(data?.message, {
          className: "text-[12px]",
        });
      setTotalPage(data?.data?.totalPages);
      setTemplateData(data?.data?.results);
      setIsLoading(false);
    });
  }, [page, limit, sortBy, searchBy, status, mandatory, reloadData]);

  // Create Form Functionality
  const [formData, setFormData] = useState({
    trainingTitle: "",
    trainingDescription: "",
    trainingEffectiveFrom: "",
    trainingLastDate: "",
    trainingStatus: "",
    // trainingScope: "",
    isMandatory: "",
    trainingFile: null,
  });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.trainingTitle.trim())
      newErrors.trainingTitle = "Training is required";
    if (!formData.trainingDescription.trim())
      newErrors.trainingDescription = "Training Description is required";
    if (!formData.trainingEffectiveFrom.trim())
      newErrors.trainingEffectiveFrom = "Training Effective From is required";
    if (!formData.trainingLastDate.trim())
      newErrors.trainingLastDate = "Training Last Date is required";
    if (!formData.trainingStatus.trim())
      newErrors.trainingStatus = "Training Status is required";
    // if (!formData.trainingScope.trim())
    //   newErrors.trainingScope = "Training Scope is required";
    if (!formData.isMandatory.trim())
      newErrors.isMandatory = "Mandatory is required";
    if (!formData.trainingFile) newErrors.trainingFile = "File is required";
    if (
      Date.parse(formData.trainingEffectiveFrom) >=
      Date.parse(formData.trainingLastDate)
    ) {
      newErrors.trainingLastDate =
        "Last date not be less than or equal to Joining Date";
    }
    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeSelect = (e, fieldName) => {
    setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: e,
    }));
  };

  const handleBlur = () => {
    validateForm();
  };

  const handleFileUpload = (event) => {
    const newErrors = { ...errors };
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFormData({
        ...formData,
        trainingFile: selectedFile,
      });
      newErrors.trainingFile = "";
    } else {
      newErrors.trainingFile = "File is required";
    }
    setErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    validateForm();

    if (Object.keys(errors).length === 0) {
      setIsLoadingButton(true);
      const formDataFinal = new FormData();
      formDataFinal.append("trainingTitle", formData.trainingTitle);
      formDataFinal.append("trainingDescription", formData.trainingDescription);
      formDataFinal.append(
        "trainingEffectiveFrom",
        formData.trainingEffectiveFrom
      );
      formDataFinal.append("trainingLastDate", formData.trainingLastDate);
      formDataFinal.append("trainingStatus", formData.trainingStatus);
      // formDataFinal.append("trainingScope", formData.trainingScope);
      formDataFinal.append("trainingFile", formData.trainingFile);
      formDataFinal.append("isMandatory", formData.isMandatory);

      createTrainingData(refreshToken, formDataFinal)
        .then((response) => {
          if (response?.code === 200 || response?.code === 201) {
            toast.success("Service Request created successfully", {
              className: "text-[12px]",
            });
          } else if (response.code === 400) {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          } else {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          }
        })
        .catch((error) => {
          toast.error("Something went wrong!", {
            className: "text-[12px]",
          });
          console.error("Error during login:", error);
        })
        .finally(() => {
          setIsLoadingButton(false);
          setFormData({
            trainingTitle: "",
            trainingDescription: "",
            trainingEffectiveFrom: "",
            trainingLastDate: "",
            trainingStatus: "",
            trainingScope: "",
            isMandatory: "",
            trainingFile: null,
          });
          setReloadData((cur) => !cur);
          setOpenCreateDialog((cur) => !cur);
        });
    }
  };
  // End Create Form Functionality

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("TrainingManagement Data");

    const headers = [
      "S.No.",
      "Title",
      "Description",
      "Effective From",
      "Completion Date",
      "Status",
      "Audience",
      "Mandatory",
    ];

    worksheet.addRow(headers);

    templateData.forEach((row, index) => {
      worksheet.addRow([
        index + 1,
        row.trainingTitle,
        row.trainingDescription,
        row.trainingEffectiveFrom,
        row.trainingLastDate,
        row.trainingStatus,
        row.trainingScope,
        row.isMandatory,
      ]);
    });

    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "trainingManagement_data.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="app-main__inner !pl-20 ">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                List of Trainings
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                View all compliance trainings
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-between md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              {!pdfView ? (
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={exportToExcel}
                  loading={isLoading === null ? true : false}
                >
                  <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
                </Button>
              ) : null}
              {!pdfView && rbac?.trainingAdd ? (
                <>
                  <Button
                    onClick={handleOpenCreateDialog}
                    className="flex items-center gap-3"
                    size="sm"
                    loading={isLoading === null ? true : false}
                  >
                    <UserPlusIcon strokeWidth={2} className="h-4 w-4" />{" "}
                    <span className="hidden md:inline-block"> Create New</span>
                  </Button>
                </>
              ) : null}
            </div>
          </div>
          {!pdfView && (
            <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
                <Select
                  color="blue"
                  className="!border-t-0 !border-[#b0bec5]"
                  label="Select Limit"
                  onChange={(event) => {
                    setLimit(event);
                    setIsLoading(null);
                  }}
                  value={limit}
                >
                  <Option value={10}>10</Option>
                  <Option value={20}>20</Option>
                  <Option value={30}>30</Option>
                  <Option value={40}>40</Option>
                  <Option value={50}>50</Option>
                </Select>
                <Select
                  color="purple"
                  className="!border-t-0 !border-[#b0bec5]"
                  label="Sort By"
                  onChange={(event) => {
                    setSortBy(event);
                    setIsLoading(null);
                  }}
                  value={sortBy}
                >
                  <Option value="asc">Ascending</Option>
                  <Option value="desc">Descending</Option>
                </Select>
                <Select
                  color="green"
                  className="!border-t-0 !border-[#b0bec5]"
                  label="Status"
                  value={status}
                  onChange={(event) => {
                    setStatus(event);
                    setIsLoading(null);
                  }}
                >
                  <Option value="">All Data</Option>
                  <Option value="schedule">Schedule</Option>
                  <Option value="inProgress">In Progress</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="onHold">On Hold</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
                <Select
                  color="green"
                  className="!border-t-0 !border-[#b0bec5]"
                  label="Mandatory"
                  onChange={(event) => {
                    setMandatory(event);
                    setIsLoading(null);
                  }}
                  value={mandatory}
                >
                  <Option value="">All Data</Option>
                  <Option value="yes">Mandatory</Option>
                  <Option value="no">Non-Mandatory</Option>
                </Select>
              </div>
              <div className="w-full md:w-auto">
                <Input
                  label="Search"
                  className="focus:!border-black focus:!border-2 focus:!border-t-0"
                  value={searchBy}
                  onChange={(e) => setSearchBy(e.target.value)}
                  icon={<MagnifyingGlassIcon className="h-5 w-5" />}
                />
              </div>
            </div>
          )}
        </CardHeader>
        {pdfView ? (
          <>
            <TrainingPdfView setPdfView={setPdfView} pdfData={pdfData} />
          </>
        ) : (
          <CardBody className="overflow-scroll px-3 p-0">
            <table className="mt-4 w-full min-w-max table-auto text-left">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={head}
                      className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50 w-36"
                    >
                      <Typography
                        variant="small"
                        color="blue-gray"
                        className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                      >
                        {head}{" "}
                        {index !== TABLE_HEAD.length - 1 && (
                          <ChevronUpDownIcon
                            strokeWidth={2}
                            className="h-4 w-4"
                          />
                        )}
                      </Typography>
                    </th>
                  ))}
                </tr>
              </thead>
              {isLoading === null ? (
                <>
                  <tbody>
                    <tr>
                      <td colSpan={TABLE_HEAD.length}>
                        <TableSkeleton />
                      </td>
                    </tr>
                  </tbody>
                </>
              ) : (
                <>
                  <tbody>
                    {isLoading === false && templateData?.length > 0 ? (
                      templateData.map((item, index) => {
                        if (!item) return null;

                        const {
                          trainingTitle,
                          trainingDescription,
                          trainingEffectiveFrom,
                          trainingLastDate,
                          trainingStatus,
                          trainingScope,
                          isMandatory,
                          fileUrl,
                          _id,
                        } = item;
                        const isLast = index === templateData.length - 1;
                        const classes = isLast
                          ? "p-3"
                          : "p-3 border-b border-blue-gray-50";

                        return (
                          <tr key={`${_id}-${index}`}>
                            <td
                              className={`${classes} flex justify-center w-44`}
                            >
                              <button
                                type="button"
                                className="flex gap-3 underline"
                                onClick={() => {
                                  handleSelect(item, trainingTitle);
                                }}
                              >
                                <Typography
                                  variant="small"
                                  color="blue-gray"
                                  className="font-normal capitalize"
                                >
                                  {trainingTitle}
                                </Typography>
                              </button>
                              <Tooltip
                                className="bg-blue-gray-500"
                                content="Copy Training Name"
                              >
                                <Button
                                  variant="text"
                                  size="sm"
                                  className="text-right !p-1 !m-0"
                                  color="black"
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      trainingTitle
                                    );
                                    toast.success("Copied!");
                                  }}
                                >
                                  <ContentCopyIcon className="h-[0.05px] w-[0.05px] !p-1 !m-0" />
                                </Button>
                              </Tooltip>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal capitalize"
                              >
                                {trainingDescription.length > 40 ? (
                                  <TableRowWithShowMore
                                    data={trainingDescription}
                                  />
                                ) : (
                                  trainingDescription
                                )}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={
                                    trainingEffectiveFrom
                                      ? moment(trainingEffectiveFrom).format(
                                          "MMMM DD, YYYY"
                                        )
                                      : ""
                                  }
                                  className="rounded-full w-fit text-xs	normal-case font-medium	"
                                />
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={
                                    trainingLastDate
                                      ? moment(trainingLastDate).format(
                                          "MMMM DD, YYYY"
                                        )
                                      : ""
                                  }
                                  className="rounded-full w-fit text-xs	normal-case font-medium	"
                                />
                              </Typography>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={trainingStatus}
                                  disabled={false}
                                  color={getStatusColor(trainingStatus)}
                                />
                              </div>
                            </td>
                            {/* <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal capitalize"
                              >
                                {trainingScope}
                              </Typography>
                            </td> */}
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal capitalize pl-3.5"
                              >
                                {isMandatory}
                              </Typography>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8">
                          <div className="flex flex-col items-center w-full">
                            <img
                              height={250}
                              width={250}
                              quality={100}
                              src="/assets/images/no-data-found.png"
                              className="w-1/4 mix-blend-darken"
                              alt=""
                            />
                            <p className="text-[18px] font-extralight">
                              No Data Found!
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </>
              )}
            </table>
          </CardBody>
        )}
        {isLoading === null ? null : !pdfView ? (
          <>
            <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page - 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPage }, (_, index) => (
                  <IconButton
                    key={index + 1}
                    variant={index + 1 === page ? "outlined" : "text"}
                    size="sm"
                    onClick={() => {
                      setPage(index + 1);
                      setIsLoading(null);
                    }}
                    disabled={totalPage > 1 ? false : true}
                  >
                    {index + 1}
                  </IconButton>
                )).reduce((acc, button, index, array) => {
                  if (
                    totalPage > 4 &&
                    (index === 1 || index === array.length - 2)
                  ) {
                    acc.push(
                      <IconButton
                        key={`ellipsis-${index}`}
                        variant="text"
                        size="sm"
                        disabled
                      >
                        ...
                      </IconButton>
                    );
                  } else {
                    acc.push(button);
                  }
                  return acc;
                }, [])}
              </div>
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page + 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Next
              </Button>
            </CardFooter>
          </>
        ) : (
          <div className="mb-10" />
        )}
      </Card>

      {/* create training */}
      <Dialog
        size="lg"
        open={openCreateDialog}
        handler={() => setOpenCreateDialog(false)}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <form onSubmit={handleSubmit}>
          <Card className="mx-auto w-full">
            <CardBody className="flex flex-col gap-4">
              <Typography variant="h4" color="blue-gray">
                Add | Training Management
              </Typography>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Typography className="mb-1" variant="h6">
                    Title
                  </Typography>
                  <Input
                    type="text"
                    label="Title"
                    size="lg"
                    name="trainingTitle"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    // value={selectedItem?.integrationProvider}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                  {errors.trainingTitle && (
                    <p className="text-red-900 text-[12px]">
                      {errors.trainingTitle}
                    </p>
                  )}
                </div>
                <div>
                  <Typography className="mb-1" variant="h6">
                    Description
                  </Typography>
                  <Input
                    type="text"
                    label="Description"
                    size="lg"
                    name="trainingDescription"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    // value={selectedItem?.integrationTitle}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                  {errors.trainingDescription && (
                    <p className="text-red-900 text-[12px]">
                      {errors.trainingDescription}
                    </p>
                  )}
                </div>
                <div>
                  <Typography className="mb-1" variant="h6">
                    Effective From
                  </Typography>
                  <Input
                    type="date"
                    name="trainingEffectiveFrom"
                    label="Date"
                    size="lg"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    // value={selectedItem?.integrationConfig[0]?.accountId}
                    className="focus:!border-black focus:!border-2 !border-t-0 focus:!border-t-0"
                  />
                  {errors.trainingEffectiveFrom && (
                    <p className="text-red-900 text-[12px]">
                      {errors.trainingEffectiveFrom}
                    </p>
                  )}
                </div>
                <div>
                  <Typography className="mb-1" variant="h6">
                    Last Date
                  </Typography>
                  <Input
                    type="date"
                    name="trainingLastDate"
                    label="End Date"
                    size="lg"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    // value={selectedItem?.integrationConfig[0]?.accountId}
                    className="focus:!border-black focus:!border-2 !border-t-0 focus:!border-t-0"
                  />
                  {errors.trainingLastDate && (
                    <p className="text-red-900 text-[12px]">
                      {errors.trainingLastDate}
                    </p>
                  )}
                </div>
                <div>
                  <Typography className="mb-1" variant="h6">
                    Status
                  </Typography>
                  <Select
                    label="Training Status"
                    onChange={(e) => handleChangeSelect(e, "trainingStatus")}
                    onBlur={handleBlur}
                    name="trainingStatus"
                    color="purple"
                  >
                    <Option value="active">Select Status</Option>
                    <Option value="schedule">Schedule</Option>
                    <Option value="inProgress">In Progress</Option>
                    <Option value="completed">Completed</Option>
                    <Option value="onHold">On Hold</Option>
                    <Option value="cancelled">Cancelled</Option>
                  </Select>
                  {errors.trainingStatus && (
                    <p className="text-red-900 text-[12px]">
                      {errors.trainingStatus}
                    </p>
                  )}
                </div>
                {/* <div>
                  <Typography className="mb-1" variant="h6">
                    Scope
                  </Typography>
                  <Input
                    type="text"
                    name="trainingScope"
                    label="Scope"
                    size="lg"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                  {errors.trainingScope && (
                    <p className="text-red-900 text-[12px]">
                      {errors.trainingScope}
                    </p>
                  )}
                </div> */}
                <div>
                  <Typography className="mb-1" variant="h6">
                    Mandatory
                  </Typography>
                  <Select
                    label="Mandatory"
                    onChange={(e) => handleChangeSelect(e, "isMandatory")}
                    onBlur={handleBlur}
                    name="mandatory"
                    color="purple"
                  >
                    <Option value="active">Select Mandatory</Option>
                    <Option value="yes">Yes</Option>
                    <Option value="no">No</Option>
                  </Select>
                  {errors.isMandatory && (
                    <p className="text-red-900 text-[12px]">
                      {errors.isMandatory}
                    </p>
                  )}
                </div>
                {/* <div>
                  <Typography className="mb-1" variant="h6">
                    Mandatory
                  </Typography>
                  <Input
                    type="text"
                    label="Mandatory"
                    name="isMandatory"
                    size="lg"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    // value={selectedItem?.integrationConfig[0]?.subscriptionId}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                  {errors.isMandatory && (
                    <p className="text-red-900 text-[12px]">
                      {errors.isMandatory}
                    </p>
                  )}
                </div> */}
                <div className="mt-2">
                  <label
                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    for="file_input"
                  >
                    Upload file
                  </label>
                  <input
                    name="trainingFile"
                    onChange={handleFileUpload}
                    accept=".pdf"
                    className="!block !w-full !text-sm !text-gray-900 !border !border-gray-300 !rounded-lg !cursor-pointer !bg-gray-50 !dark:text-gray-400 !focus:outline-none !dark:bg-gray-700 !dark:border-gray-600 !dark:placeholder-gray-400"
                    id="file_input"
                    type="file"
                  />
                  {errors.trainingFile && (
                    <p className="text-red-900 text-[12px]">
                      {errors.trainingFile}
                    </p>
                  )}
                </div>
              </div>
            </CardBody>
            <CardFooter className="flex justify-end border-t-2 py-2">
              <Button
                variant="text"
                color="red"
                onClick={handleOpenCreateDialog}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>
              {isLoadingButton ? (
                <>
                  <LoadingButton isSetText={true} isLoading={isLoading} />
                </>
              ) : (
                <Button variant="gradient" type="submit" color="green">
                  <span>Submit</span>
                </Button>
              )}
            </CardFooter>
          </Card>
        </form>
      </Dialog>
    </div>
  );
}
